import React, { useState, useEffect, useRef } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Sparkles, Loader2, Send, Upload, X, Ruler, MessageSquare, Camera, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/apiClient";

// Reusable interior-design assistant: room description, design-sample uploads,
// AI measure-from-photo, and an AI chat + suggestion chips. Reports data up via onChange.
export default function InteriorAssist({ roomFile, style = "modern", accent = "#F59E0B", onChange }) {
  const [roomDesc, setRoomDesc] = useState("");
  const [samples, setSamples] = useState([]); // {file, url}
  const [floorplan, setFloorplan] = useState(null); // {file, url}
  const [angles, setAngles] = useState([]); // [{file,url}] same room, other angles
  const [width, setWidth] = useState("");
  const [length, setLength] = useState("");
  const [height, setHeight] = useState("");
  const [brief, setBrief] = useState("");
  const [measuring, setMeasuring] = useState(false);
  const [chat, setChat] = useState([]); // {role, text}
  const [chips, setChips] = useState([]);
  const [msg, setMsg] = useState("");
  const [asking, setAsking] = useState(false);
  const sampleRef = useRef(null);
  const fpRef = useRef(null);
  const anglesRef = useRef(null);

  useEffect(() => {
    onChange && onChange({
      roomDesc, samples: samples.map((s) => s.file),
      floorplan: floorplan?.file || null,
      angles: angles.map((a) => a.file),
      width, length, height, brief,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomDesc, samples, floorplan, angles, width, length, height, brief]);

  const addAngles = (files) => {
    const list = Array.from(files || []).filter((x) => /(jpeg|jpg|png|webp)/i.test(x.type));
    if (!list.length) return;
    setAngles((prev) => [...prev, ...list.map((f) => ({ file: f, url: URL.createObjectURL(f) }))].slice(0, 4));
  };

  const addFloorplan = (files) => {
    const f = Array.from(files || []).find((x) => /(jpeg|jpg|png|webp)/i.test(x.type));
    if (!f) return;
    setFloorplan({ file: f, url: URL.createObjectURL(f) });
  };

  const addSamples = (files) => {
    const arr = Array.from(files || []).filter((f) => /(jpeg|jpg|png|webp)/i.test(f.type));
    if (!arr.length) return;
    setSamples((s) => [...s, ...arr.map((f) => ({ file: f, url: URL.createObjectURL(f) }))].slice(0, 4));
  };
  const removeSample = (i) => setSamples((s) => s.filter((_, j) => j !== i));

  const measure = async () => {
    if (!roomFile) { toast.error("Add your room photo first"); return; }
    setMeasuring(true);
    try {
      const fd = new FormData();
      fd.append("file", roomFile);
      const { data } = await api.post(`/ai/measure-photo?room=${encodeURIComponent(roomDesc || "room")}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const o = data.overall || {};
      if (o.width_ft) setWidth(String(Math.round(o.width_ft)));
      if (o.length_ft) setLength(String(Math.round(o.length_ft)));
      if (o.height_ft) setHeight(String(Math.round(o.height_ft)));
      toast.success("AI estimated the room size — tweak if needed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not estimate size");
    } finally { setMeasuring(false); }
  };

  const send = async () => {
    const text = msg.trim();
    if (!text) return;
    const next = [...chat, { role: "user", text }];
    setChat(next); setMsg(""); setAsking(true);
    try {
      const { data } = await api.post("/ai/design-assist", {
        room: roomDesc || "room", message: text, style,
        history: chat.map((c) => ({ role: c.role, text: c.text })),
      });
      setChat([...next, { role: "assistant", text: data.reply }]);
      setChips(data.suggestions || []);
      if (data.brief) setBrief(data.brief);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Assistant unavailable");
      setChat(next);
    } finally { setAsking(false); }
  };

  const applyChip = (c) => {
    setBrief((b) => (b ? `${b}; ${c}` : c));
    toast.success(`Added: ${c}`);
  };

  return (
    <div className="rounded-xl border border-[#21262D] p-4 space-y-4" data-testid="interior-assist">
      <div>
        <Label className="text-xs text-[#8B949E]">Room you're working on</Label>
        <Input value={roomDesc} onChange={(e) => setRoomDesc(e.target.value)} placeholder="e.g. primary bedroom, kitchen, home office"
          className="mt-1 bg-[#0D1117] border-[#30363D]" data-testid="assist-room-desc" />
      </div>

      <div>
        <div className="flex items-center justify-between">
          <Label className="text-xs text-[#8B949E]">Design samples (up to 4 — AI fits them to your room)</Label>
          <button onClick={() => sampleRef.current?.click()} disabled={samples.length >= 4}
            className="text-[11px] hover:underline flex items-center gap-1 disabled:opacity-40" style={{ color: accent }} data-testid="assist-add-sample">
            <Upload className="w-3 h-3" /> Add
          </button>
        </div>
        <input ref={sampleRef} type="file" accept="image/*" multiple className="hidden" data-testid="assist-sample-input" onChange={(e) => addSamples(e.target.files)} />
        <div className="grid grid-cols-4 gap-2 mt-1.5">
          {samples.map((s, i) => (
            <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-[#30363D]" data-testid={`assist-sample-${i}`}>
              <img src={s.url} alt={`sample ${i + 1}`} className="w-full h-full object-cover" />
              <button onClick={() => removeSample(i)} className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 flex items-center justify-center hover:bg-red-500/80" data-testid={`assist-sample-remove-${i}`}>
                <X className="w-3 h-3 text-white" />
              </button>
            </div>
          ))}
          {samples.length === 0 && (
            <button onClick={() => sampleRef.current?.click()} className="aspect-square rounded-lg border border-dashed border-[#30363D] flex flex-col items-center justify-center text-[10px] text-[#6E7681] hover:border-[#484F58] col-span-4 py-4" data-testid="assist-sample-empty">
              <Upload className="w-4 h-4 mb-1" /> Add inspiration photos
            </button>
          )}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between">
          <Label className="text-xs text-[#8B949E] flex items-center gap-1.5"><Camera className="w-3.5 h-3.5" style={{ color: accent }} /> Same room — other angles (up to 4, optional)</Label>
        </div>
        <input ref={anglesRef} type="file" accept="image/*" multiple className="hidden" data-testid="assist-angles-input" onChange={(e) => addAngles(e.target.files)} />
        <div className="mt-1.5 grid grid-cols-4 gap-1.5">
          {angles.map((a, i) => (
            <div key={i} className="relative rounded-lg overflow-hidden border border-[#30363D] group" data-testid={`assist-angle-${i}`}>
              <img src={a.url} alt={`angle ${i + 1}`} className="w-full h-16 object-cover bg-[#0D1117]" />
              <button onClick={() => setAngles((p) => p.filter((_, j) => j !== i))} className="absolute top-0.5 right-0.5 bg-black/60 rounded p-0.5" data-testid={`assist-angle-remove-${i}`}>
                <X className="w-3 h-3 text-white" />
              </button>
            </div>
          ))}
          {angles.length < 4 && (
            <button onClick={() => anglesRef.current?.click()} className="rounded-lg border border-dashed border-[#30363D] h-16 flex items-center justify-center text-[#6E7681] hover:border-[#484F58]" data-testid="assist-angles-add">
              <Plus className="w-4 h-4" />
            </button>
          )}
        </div>
        <p className="mt-1 text-[10px] text-[#6E7681]">More angles of the same room = a more accurate, true-to-life render.</p>
      </div>

      <div>
        <div className="flex items-center justify-between">
          <Label className="text-xs text-[#8B949E] flex items-center gap-1.5"><Ruler className="w-3.5 h-3.5" style={{ color: accent }} /> Floor plan (optional — render follows this layout)</Label>
          {floorplan && (
            <button onClick={() => setFloorplan(null)} className="text-[11px] hover:underline flex items-center gap-1 text-[#8B949E]" data-testid="assist-fp-remove">
              <X className="w-3 h-3" /> Remove
            </button>
          )}
        </div>
        <input ref={fpRef} type="file" accept="image/*" className="hidden" data-testid="assist-fp-input" onChange={(e) => addFloorplan(e.target.files)} />
        {floorplan ? (
          <div className="mt-1.5 relative rounded-lg overflow-hidden border border-[#30363D]" data-testid="assist-fp-preview">
            <img src={floorplan.url} alt="floor plan" className="w-full max-h-40 object-contain bg-[#0D1117]" />
          </div>
        ) : (
          <button onClick={() => fpRef.current?.click()} className="mt-1.5 w-full rounded-lg border border-dashed border-[#30363D] flex flex-col items-center justify-center text-[10px] text-[#6E7681] hover:border-[#484F58] py-4" data-testid="assist-fp-empty">
            <Upload className="w-4 h-4 mb-1" /> Add a floor plan (drawing or photo) — the AI places furniture to match it
          </button>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between">
          <Label className="text-xs text-[#8B949E]">Room size (ft)</Label>
          <button onClick={measure} disabled={measuring || !roomFile} className="text-[11px] hover:underline flex items-center gap-1 disabled:opacity-40" style={{ color: accent }} data-testid="assist-measure-btn">
            {measuring ? <Loader2 className="w-3 h-3 animate-spin" /> : <Ruler className="w-3 h-3" />} AI measure from photo
          </button>
        </div>
        <div className="grid grid-cols-3 gap-2 mt-1">
          <Input value={width} onChange={(e) => setWidth(e.target.value)} type="number" placeholder="Width" className="bg-[#0D1117] border-[#30363D]" data-testid="assist-width" />
          <Input value={length} onChange={(e) => setLength(e.target.value)} type="number" placeholder="Length" className="bg-[#0D1117] border-[#30363D]" data-testid="assist-length" />
          <Input value={height} onChange={(e) => setHeight(e.target.value)} type="number" placeholder="Ceiling" className="bg-[#0D1117] border-[#30363D]" data-testid="assist-height" />
        </div>
      </div>

      <div>
        <Label className="text-xs text-[#8B949E] flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5" style={{ color: accent }} /> AI design assistant</Label>
        <div className="mt-1.5 rounded-lg border border-[#30363D] bg-[#0D1117] p-2.5 max-h-44 overflow-y-auto space-y-2" data-testid="assist-chat">
          {chat.length === 0 && <p className="text-[11px] text-[#6E7681]">Describe what you want (mood, colours, must-haves) and I'll suggest ideas that fit your room.</p>}
          {chat.map((c, i) => (
            <div key={i} className={`text-xs ${c.role === "user" ? "text-[#F0F6FC]" : "text-[#8B949E]"}`} data-testid={`assist-msg-${i}`}>
              <span className="font-semibold" style={{ color: c.role === "assistant" ? accent : undefined }}>{c.role === "user" ? "You" : "Designer"}:</span> {c.text}
            </div>
          ))}
          {asking && <div className="flex items-center gap-1.5 text-[11px] text-[#6E7681]"><Loader2 className="w-3 h-3 animate-spin" /> thinking…</div>}
        </div>
        {chips.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2" data-testid="assist-chips">
            {chips.map((c, i) => (
              <button key={i} onClick={() => applyChip(c)} className="text-[11px] px-2 py-1 rounded-full border border-[#30363D] text-[#8B949E] hover:text-[#F0F6FC] hover:border-[#484F58]" data-testid={`assist-chip-${i}`}>
                + {c}
              </button>
            ))}
          </div>
        )}
        <div className="flex gap-2 mt-2">
          <Input value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }}
            placeholder="e.g. warm minimalist with walnut and cream" className="bg-[#0D1117] border-[#30363D]" data-testid="assist-input" />
          <Button onClick={send} disabled={asking || !msg.trim()} size="sm" className="font-semibold text-[#0D1117]" style={{ background: accent }} data-testid="assist-send-btn">
            {asking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {brief && (
        <div className="rounded-lg border border-[#30363D] bg-[#0D1117] p-2.5" data-testid="assist-brief">
          <div className="text-[10px] uppercase tracking-widest text-[#6E7681] mb-1 flex items-center gap-1"><Sparkles className="w-3 h-3" style={{ color: accent }} /> Design brief (used for generation)</div>
          <textarea value={brief} onChange={(e) => setBrief(e.target.value)} rows={2} className="w-full bg-transparent text-xs text-[#F0F6FC] resize-none outline-none" data-testid="assist-brief-text" />
        </div>
      )}
    </div>
  );
}
